import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Switch } from 'antd';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_CONTENT_INSET } from '../../constants/AIAgentConstants';
import { OSCAR_ICON_SIZE } from '../../constants/AIAgentConstants';
import OscarIconButton from '../shared/OscarIconButton';

interface OscarContextBarProps {
	/** What the agent can currently see, e.g. "Sharing: WO #34234/ RFP bids". */
	summary: React.ReactNode;
	/** Optional inline switch, e.g. "Show RFP Logs". Only shown while expanded. */
	toggleLabel?: string;
	toggleChecked?: boolean;
	onToggleChange?: (checked: boolean) => void;
	/** Collapsed hides the context controls and points the chevron down. */
	collapsed?: boolean;
	/** Omit to hide the chevron entirely (bar becomes a static summary). */
	onToggleCollapsed?: () => void;
	className?: string;
}

/**
 * The strip pinned directly above the composer, telling the user which records
 * the agent can currently see.
 *
 * Two designed states, and the chevron moves between them:
 * - collapsed — Figma `15111:53235`: chevron down, context controls hidden
 * - expanded  — Figma `14998:125334`: chevron up, controls visible
 *
 * Figma hides the control group with `opacity-0`, which would reserve its width
 * and leave a phantom gap in this flex row; it is unmounted instead.
 */
export const OscarContextBar = ({
	summary,
	toggleLabel,
	toggleChecked,
	onToggleChange,
	collapsed,
	onToggleCollapsed,
	className,
}: OscarContextBarProps) => (
	<div
		className={cn(
			// shrink-0 keeps the bar pinned — it must not compress as the
			// conversation above it grows.
			'flex w-full shrink-0 items-center gap-xs bg-grey-2/70 py-xs text-character-secondary',
			OSCAR_CONTENT_INSET,
			className
		)}
	>
		<div className="footnoteregular min-w-0 flex-1 truncate">{summary}</div>
		<div className="flex shrink-0 items-center gap-2.5">
			{toggleLabel && !collapsed ? (
				<div className="flex items-center gap-2xs">
					<span className="footnoteregular whitespace-nowrap">{toggleLabel}</span>
					<Switch size="small" checked={!!toggleChecked} onChange={onToggleChange} />
				</div>
			) : null}
			{onToggleCollapsed ? (
				<OscarIconButton
					icon={collapsed ? <DownOutlined /> : <UpOutlined />}
					label={collapsed ? 'Expand context' : 'Collapse context'}
					onClick={onToggleCollapsed}
					size={OSCAR_ICON_SIZE.chevron}
					className="text-character-secondary"
				/>
			) : null}
		</div>
	</div>
);

export default OscarContextBar;
